import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, notification, Row, Select, Typography, Icon } from 'antd'
import moment from 'moment'

import SearchCultivarsHistory from './SearchCultivarsHistory'
import CreateCultivarsHistory from './CreateCultivarsHistory'
import ListCultivars from './ListCultivars'
import SumCultivars from './SumCultivars'
import { api, request } from '../../../util/handlerRequestUtil'
import { getMessage } from '../../../util/IntlMessages'
import { sumCultivarsProperties } from '../../../util/sumCultivarsProperties'
import { sanitizeCultivarItem } from '../../../util/sanitizeCultivarItem'

import './Cultivars.css'

const cultivarsinitialState = {
  totalArea: 0,
  checkArea: 0,
  i2xArea: 0,
  i2xOpportunity: 0,
  organizationArea: 0,
  organizationOpportunity: 0,
  cultivarsItems: []
}

const CultivarsHistory = ({ currentUser, customerId, districts }) => {
  const [materials, setMaterials] = useState([])
  const [demandTypes, setDemandTypes] = useState([])
  const [cultivarsTypes, setCultivarsTypes] = useState([])
  const [cultivarsTypesCheck, setCultivarsTypesCheck] = useState()
  const [selectedDistrict, setSelectedDistrict] = useState()
  const [cultivars, setCultivars] = useState(cultivarsinitialState)

  const [formattedYears, setFormattedYears] = useState([])
  const [formattedMonths, setFormattedMonths] = useState([])
  const [cultivarDate, setCultivarDate] = useState('')

  useEffect(() => {
    fetchMaterials()
    fetchDemandTypes()
    fetchCultivarsTypes()
    fetchCheckId()
  }, [])

  useEffect(() => {
    const { cultivarsItems } = cultivars

    const totalArea = sumCultivarsProperties(cultivarsItems)
    const checkArea = sumCultivarsProperties(
      cultivarsItems,
      (item) => item.cultivarsDemandTypeId === cultivarsTypesCheck
    )
    let i2xAreaArray = cultivarsItems.filter((cultivar) => {
      if (cultivar.materials && cultivar.materials.tecnologia.name === 'i2x') {
        return cultivar
      }
    })
    const i2xArea = i2xAreaArray.reduce((acc, item) => acc + Number(item.area), 0)
    const organizationArea = 0

    setCultivars({
      ...cultivars,
      totalArea,
      checkArea,
      i2xArea,
      organizationArea,
      i2xOpportunity: totalArea - i2xArea,
      organizationOpportunity: totalArea - organizationArea,
      cultivarsDate: cultivarDate
    })

    const currentYear = moment().year()

    const years = []
    for (let index = currentYear - 2; index <= currentYear; index++) {
      years.push({ value: index.toString(), description: index.toString() })
    }

    if (years.length > 0) setFormattedYears(years)

    const months = []

    for (let index = 1; index <= 12; index++) {
      index < 10
        ? months.push({ value: index.toString(), description: '0' + index.toString() })
        : months.push({ value: index.toString(), description: index.toString() })
    }

    if (months.length > 0) setFormattedMonths(months)
  }, [cultivars.cultivarsItems, currentUser])

  async function fetchMaterials() {
    const materialsList = await request(api.getMaterialsWithTecnologia())
    setMaterials(materialsList)
  }

  async function fetchDemandTypes() {
    const demandTypesList = await request(api.getCultivarsDemandTypesDropdown({ todos: false, descricao: true }))
    setDemandTypes(demandTypesList)
  }

  async function fetchCultivarsTypes() {
    const cultivarsTypesList = await request(api.getCultivarsTypesDropdown({ todos: false, descricao: true }))
    setCultivarsTypes(cultivarsTypesList)
  }

  async function fetchCheckId() {
    const [cultivarsTypesCheck] = await request(api.getCultivarsDemandTypes({ name: 'CHECK' }))
    setCultivarsTypesCheck(cultivarsTypesCheck.id)
  }

  function attachMaterialInCultivarsItem(item) {
    const [material] = materials.filter((mat) => mat.id === item.materialsId)
    return { ...item, materials: material }
  }

  async function handleCultivarDate(value) {
    const { cultivarYear, cultivarMonth } = value

    const date = moment()
      .year(cultivarYear)
      .month(cultivarMonth - 1)
      .date(moment().format('DD'))

    const formattedDate = date.toISOString()
    setCultivarDate(formattedDate)

    const response = await request(
      api.getCultivarsHistoryByCustomer(customerId, { cultivarDate: formattedDate, districtId: selectedDistrict })
    )

    if (response) {
      setCultivars(response)
    } else {
      setCultivars({
        totalArea: 0,
        checkArea: 0,
        i2xArea: 0,
        i2xOpportunity: 0,
        organizationArea: 0,
        organizationOpportunity: 0,
        cultivarsItems: [],
        cultivarsDate: formattedDate
      })
    }
  }

  function pushCultivarsItem(item) {
    const cultivarsItem = attachMaterialInCultivarsItem(item)
    let cultivarsItems = []
    if (cultivars.cultivarsItems) {
      cultivarsItems = [...cultivars.cultivarsItems, cultivarsItem]
    } else {
      cultivarsItems = [cultivarsItem]
    }
    setCultivars({ ...cultivars, cultivarsItems })
  }

  function removeCultivarsItem(ids = []) {
    const cultivarsItems = cultivars.cultivarsItems.filter((item) => !ids.includes(item.id))
    setCultivars({ ...cultivars, cultivarsItems })
  }

  const onUpdateCultivarsItem = useCallback(
    (field, cultivarsId, value) => {
      const cultivarsItems = cultivars.cultivarsItems.map((item) => {
        if (cultivarsId && item.id === cultivarsId) {
          return { ...item, [field]: value }
        }
        return item
      })
      setCultivars({ ...cultivars, cultivarsItems })
    },
    [materials, cultivars.cultivarsItems]
  )

  function buildRequestPayload() {
    let cultivarsFormated = {
      id: cultivars.id,
      customerId: +customerId,
      districtId: selectedDistrict,
      cultivarsDate: cultivars.cultivarsDate
    }
    let cultivarsItems = []
    if (cultivars.cultivarsItems) {
      cultivarsItems = cultivars.cultivarsItems.map(sanitizeCultivarItem)
    }
    return { ...cultivarsFormated, cultivarsItems }
  }

  function createCultivars(payload) {
    return request(api.createCultivars(payload))
  }

  function updateCultivars({ id, ...payload }) {
    return request(api.updateCultivars(id, payload))
  }

  async function onSubmit() {
    const payload = buildRequestPayload()

    try {
      if (payload.id) {
        await updateCultivars(payload)
      } else {
        await createCultivars(payload)
      }

      notification.success({
        description: getMessage('successUpdateCultivars'),
        message: getMessage('sweetAlerts.success')
      })
    } catch (e) {
      console.tron.log(e)
    }
  }

  const formattedMaterials = useMemo(
    () => materials.map(({ id, name }) => ({ value: id, description: name })),
    [materials]
  )

  const selectedDistrictDescription = useMemo(
    () => (districts || []).find((district) => district.value === selectedDistrict)?.description,
    [selectedDistrict]
  )

  const onSelectDistrict = useCallback(
    (districtId) => {
      setSelectedDistrict(districtId)
      setCultivars(cultivarsinitialState)
      setCultivarDate('')
    },
    [setSelectedDistrict, setCultivars, setCultivarDate]
  )

  if (!selectedDistrict && districts.length > 0) {
    if (districts.length > 1) {
      return (
        <Select
          style={{ width: 300, margin: '48px 0 48px 50%', transform: 'translate(-50%)' }}
          placeholder="Selecione um distrito"
          onSelect={onSelectDistrict}
        >
          {districts.map(({ value, description }) => (
            <Select.Option key={value} value={value}>
              <p style={{ margin: '0px', marginLeft: '5px' }}>{description}</p>
            </Select.Option>
          ))}
        </Select>
      )
    } else {
      setSelectedDistrict(districts[0].value)
    }
  }

  return (
    <div className="cultivars">
      <Row type="flex" justify="space-between" align="center" style={{ padding: '2%' }}>
        <Typography.Title level={4} style={{ margin: 0, lineHeight: 2 }}>
          Distrito: {selectedDistrictDescription}
        </Typography.Title>
        <Row type="flex">
          {districts.length > 1 && (
            <Button type="secondary" className="button-action" onClick={() => setSelectedDistrict(null)}>
              <Icon type="left" theme="outlined" className="default-back-icon" />
              {getMessage('label.back')}
            </Button>
          )}
          <Button type="primary" className="button-action" onClick={onSubmit}>
            {cultivars.id ? getMessage('update') : getMessage('register')}
          </Button>
        </Row>
      </Row>

      <SearchCultivarsHistory
        filterCultivarsByDate={(value) => handleCultivarDate(value)}
        customerId={customerId}
        formattedYears={formattedYears}
        formattedMonths={formattedMonths}
        selectedDistrictId={selectedDistrict}
      />

      {cultivarDate !== '' ? (
        <>
          <CreateCultivarsHistory
            onCreateCultivar={(value) => pushCultivarsItem(value)}
            formattedMaterials={formattedMaterials}
            formattedDemandTypes={demandTypes}
            formattedCultivarsTypes={cultivarsTypes}
          />

          <ListCultivars
            cultivarsItems={cultivars.cultivarsItems}
            onRemoveCultivarItems={removeCultivarsItem}
            formattedMaterials={formattedMaterials}
            formattedDemandTypes={demandTypes}
            formattedCultivarsTypes={cultivarsTypes}
            onUpdateCultivarsItem={onUpdateCultivarsItem}
          />

          <SumCultivars organizationId={currentUser.organizationId} cultivars={cultivars} />
        </>
      ) : null}
    </div>
  )
}

export default CultivarsHistory
